import { useState } from 'react';

import { Avatar, colorForSeed, initialsFor } from '../conversations/Avatar';
import { SshKeyList } from '../sftp/SshKeyList';
import { assignableRoles } from './role-utils';
import type { CapabilityMap, RoleResponse, TenantUserResponse } from '../../lib/api-client';
import type { UpdateUserBody } from '../../hooks/use-users';

interface Props {
  user: TenantUserResponse;
  roles: RoleResponse[];
  canManage: boolean;
  actorCapabilities: CapabilityMap;
  isSelf: boolean;
  onClose: () => void;
  onUpdate: (body: UpdateUserBody) => Promise<void>;
  onDeactivate: () => Promise<void>;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function UserDetailPopover({
  user,
  roles,
  canManage,
  actorCapabilities,
  isSelf,
  onClose,
  onUpdate,
  onDeactivate,
}: Props) {
  const [displayName, setDisplayName] = useState(user.display_name ?? '');
  const [roleId, setRoleId] = useState<string>(user.role_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  const isDeactivated = !!user.deactivated_at;
  const dirty = displayName !== (user.display_name ?? '') || roleId !== (user.role_id ?? '');
  const displayable = user.display_name ?? user.email.split('@')[0] ?? user.email;

  // Roles the actor may assign (capabilities subset rule). Always include the
  // user's CURRENT role so it shows even if the actor couldn't re-grant it.
  const options = (() => {
    const allowed = assignableRoles(roles, actorCapabilities);
    if (user.role_id && !allowed.some((r) => r.id === user.role_id)) {
      const current = roles.find((r) => r.id === user.role_id);
      if (current) return [current, ...allowed];
    }
    return allowed;
  })();

  // Skills now attach to ROLES, managed in the Roles view — not per user.
  // Surface a hint pointing there.
  const roleHasCreator = !!user.role_capabilities?.creator_mode || !!user.role_capabilities?.full_access;

  const handleSave = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body: UpdateUserBody = {};
      if (displayName !== (user.display_name ?? '')) body.display_name = displayName.trim() || undefined;
      if (roleId !== (user.role_id ?? '')) body.role_id = roleId;
      await onUpdate(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onDeactivate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
      setBusy(false);
      setConfirmingDeactivate(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 12,
          padding: '18px 20px 20px',
          width: 460,
          maxWidth: 'calc(100vw - 40px)',
          boxShadow: 'var(--shadow)',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Avatar initials={initialsFor(displayable)} color={colorForSeed(user.id)} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{displayable}</div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{user.email}</div>
          </div>
          {isDeactivated && (
            <span
              style={{
                fontSize: 10,
                color: 'var(--kind-failure)',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                padding: '3px 8px',
                background: 'rgba(192,57,43,0.08)',
                border: '1px solid rgba(192,57,43,0.2)',
                borderRadius: 999,
                fontWeight: 500,
              }}
            >
              Deactivated
            </span>
          )}
        </div>

        {/* Display name */}
        <Label>Display name</Label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={!canManage || busy}
          placeholder={user.email}
          style={inputStyle(!canManage)}
        />

        <div style={{ height: 14 }} />

        {/* Role — one role per user (capability RBAC). */}
        <Label>Role</Label>
        <select
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          disabled={!canManage || busy || isSelf}
          title={isSelf ? "Can't change your own role" : undefined}
          style={{ ...inputStyle(!canManage || isSelf), cursor: !canManage || isSelf ? 'not-allowed' : 'pointer' }}
        >
          {options.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        {/* SSH keys — the user's SFTP identity; shown to the user and to admins over their tree. */}
        {(canManage || isSelf) && (
          <div data-testid="popover-ssh-keys" style={{ marginTop: 14 }}>
            <Label>SSH keys</Label>
            <SshKeyList userId={user.id} userLabel={user.email} canManage={canManage || isSelf} variant="compact" />
          </div>
        )}

        {/* Skills attach to ROLES now — managed in the Roles view, not per user. */}
        {canManage && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 10px',
              background: 'var(--bg)',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              fontSize: 11,
              color: 'var(--text-soft)',
              lineHeight: 1.5,
            }}
          >
            {roleHasCreator
              ? "This user's role can use Creator Mode and has access to all skills."
              : 'Skill access is managed per role in the Roles view. This user inherits the skills granted to their role.'}
          </div>
        )}

        {/* Meta */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
          <Metadata label="Joined" value={fmtDate(user.created_at)} />
          <Metadata label="Last updated" value={fmtDate(user.updated_at)} />
          <Metadata label="User ID" value={user.id} mono />
          {user.deactivated_at && <Metadata label="Deactivated" value={fmtDate(user.deactivated_at)} />}
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: '6px 10px',
              background: 'rgba(192,57,43,0.06)',
              border: '1px solid rgba(192,57,43,0.18)',
              borderRadius: 4,
              fontSize: 11,
              color: 'var(--kind-failure)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'space-between' }}>
          <div>
            {canManage && !isDeactivated && !isSelf && !confirmingDeactivate && (
              <button
                type="button"
                onClick={() => setConfirmingDeactivate(true)}
                disabled={busy}
                style={{
                  padding: '7px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  background: 'transparent',
                  color: 'var(--kind-failure)',
                  border: '1px solid rgba(192,57,43,0.3)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Deactivate
              </button>
            )}
            {confirmingDeactivate && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: 'var(--kind-failure)' }}>Confirm?</span>
                <button
                  type="button"
                  onClick={handleDeactivate}
                  disabled={busy}
                  style={{
                    padding: '7px 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    background: 'var(--kind-failure)',
                    color: '#fff',
                    border: 0,
                    borderRadius: 6,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {busy ? 'Deactivating…' : 'Yes, deactivate'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDeactivate(false)}
                  disabled={busy}
                  style={secondaryStyle}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy} style={secondaryStyle}>
              Close
            </button>
            {canManage && (
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || busy}
                style={{
                  padding: '7px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 0,
                  borderRadius: 6,
                  cursor: !dirty || busy ? 'not-allowed' : 'pointer',
                  opacity: !dirty || busy ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--text-soft)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        fontWeight: 600,
        marginBottom: 5,
      }}
    >
      {children}
    </div>
  );
}

function Metadata({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text)',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function inputStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '8px 10px',
    fontSize: 13,
    fontFamily: 'inherit',
    background: 'var(--bg)',
    border: '1px solid var(--border-strong)',
    borderRadius: 6,
    color: 'var(--text)',
    outline: 'none',
    opacity: disabled ? 0.6 : 1,
  };
}

const secondaryStyle = {
  padding: '7px 14px',
  fontSize: 12,
  fontWeight: 500,
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
} satisfies React.CSSProperties;
